import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Loader, Copy, CheckCircle, Clock, XCircle, ExternalLink, RefreshCw, Edit } from 'lucide-react';
import { paymentPlansApi } from '../api/client';

const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { color: 'bg-yellow-900 bg-opacity-50 text-yellow-300', icon: Clock, label: 'Pendente' },
    paid: { color: 'bg-green-900 bg-opacity-50 text-green-300', icon: CheckCircle, label: 'Pago' },
    partially_paid: { color: 'bg-blue-900 bg-opacity-50 text-blue-300', icon: Clock, label: 'Parcialmente Pago' },
    failed: { color: 'bg-red-900 bg-opacity-50 text-red-300', icon: XCircle, label: 'Falhou' },
    cancelled: { color: 'bg-gray-700 text-gray-300', icon: XCircle, label: 'Cancelado' },
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      <Icon className="h-3 w-3 mr-1" />
      {config.label}
    </span>
  );
};

const PaymentStatus = () => {
  const { planId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(location.state?.plan || null);
  const [loading, setLoading] = useState(!plan);
  const [refreshing, setRefreshing] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [error, setError] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [reconcileMessage, setReconcileMessage] = useState(null);

  useEffect(() => {
    if (!plan) {
      loadPlan();
    }
  }, [planId]);

  const loadPlan = async () => {
    try {
      const response = await paymentPlansApi.getById(planId);
      setPlan(response.data.data);
    } catch (err) {
      setError('Failed to load payment plan');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setReconcileMessage(null);
    
    // First reconcile with iPag to check for any missed payments
    try {
      setReconciling(true);
      const reconcileResponse = await paymentPlansApi.reconcile(planId);
      
      if (reconcileResponse.data.success) {
        const { reconciledCount, totalPending } = reconcileResponse.data;
        
        if (reconciledCount > 0) {
          setReconcileMessage({
            type: 'success',
            text: `✅ Found and updated ${reconciledCount} payment(s) from iPag`
          });
        } else if (totalPending > 0) {
          setReconcileMessage({
            type: 'info',
            text: `ℹ️ Checked iPag - ${totalPending} payment(s) still pending`
          });
        }
      }
    } catch (reconcileErr) {
      console.error('Reconciliation error:', reconcileErr);
      // Don't show error to user, just continue with refresh
    } finally {
      setReconciling(false);
    }
    
    // Then refresh the plan data
    await loadPlan();
    
    // If plan is paid, also retry e-commerce sync (order approval)
    if (plan?.status === 'paid') {
      try {
        const retryResponse = await paymentPlansApi.retryEcommerceSync(planId);
        
        if (retryResponse.data.success) {
          const { tiendanubeApproval } = retryResponse.data.results;
          
          if (tiendanubeApproval?.success) {
            setReconcileMessage({
              type: 'success',
              text: `✅ Order approved in e-commerce successfully`
            });
          }
        }
      } catch (retryErr) {
        console.error('E-commerce sync retry error:', retryErr);
        // Don't show error to user
      }
    }
  };

  const copyToClipboard = (text, index) => {
    console.log('Copying to clipboard:', text);
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => {
          setCopiedIndex(index);
          setTimeout(() => setCopiedIndex(null), 2000);
        })
        .catch(() => {
          fallbackCopyToClipboard(text, index);
        });
    } else {
      fallbackCopyToClipboard(text, index);
    }
  };

  const fallbackCopyToClipboard = (text, index) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Could not copy automatically. Please copy manually:\n\n' + text);
    }
    
    document.body.removeChild(textArea);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (error || !plan) {
    return (
      <div className="alert-error">
        <p className="text-sm text-red-200">{error || 'Plano de pagamento não encontrado'}</p>
      </div>
    );
  }

  const planData = plan.plan || plan;
  const parts = plan.parts || [];

  return (
    <div className="px-4 sm:px-0">
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-white">Status do Plano de Pagamento</h1>
          <p className="mt-2 text-sm text-gray-400">
            Pedido #{planData.order_number} - ID do Plano: {planData.id}
          </p>
        </div>
        <div className="flex gap-2">
          {(planData.status === 'pending' || planData.status === 'partially_paid') && (
            <button
              onClick={() => navigate(`/payment-plans/${planId}/edit`)}
              className="inline-flex items-center px-3 py-2 border border-yellow-600 shadow-sm text-sm font-medium rounded-md text-yellow-300 bg-yellow-900 bg-opacity-50 hover:bg-opacity-70 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
            >
              <Edit className="h-4 w-4 mr-2" />
              Editar Plano
            </button>
          )}
          <button
            onClick={handleRefresh}
            disabled={refreshing || reconciling}
            className="btn-secondary inline-flex items-center disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${(refreshing || reconciling) ? 'animate-spin' : ''}`} />
            {reconciling ? 'Checking iPag...' : refreshing ? 'Refreshing...' : 'Refresh & Check iPag'}
          </button>
        </div>
      </div>

      {reconcileMessage && (
        <div className={`mb-6 rounded-lg p-4 ${
          reconcileMessage.type === 'success' ? 'alert-success' : 'alert-info'
        }`}>
          <p className={`text-sm ${
            reconcileMessage.type === 'success' ? 'text-green-200' : 'text-blue-200'
          }`}>
            {reconcileMessage.text}
          </p>
        </div>
      )}

      <div className="card card-body mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-sm font-medium text-gray-400">Total do Pedido</p>
            <p className="mt-1 text-2xl font-semibold text-primary-500">
              {planData.currency} {parseFloat(planData.order_total).toFixed(2)}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-400">Status do Plano</p>
            <div className="mt-1">
              <StatusBadge status={planData.status} />
            </div>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="card-header">
          <h2 className="text-lg font-medium text-white">Links de Pagamento</h2>
          <p className="mt-1 text-sm text-gray-400">
            Compartilhe estes links com o cliente para completar o pagamento
          </p>
        </div>

        <div className="divide-y divide-dark-600">
          {parts.map((part, index) => {
            console.log('Part data:', { id: part.id, payment_link_url: part.payment_link_url, reference: part.reference });
            return (
            <div key={part.id} className="px-6 py-4 bg-dark-700">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-white">
                      {part.label || `Part ${part.sequence}`}
                    </span>
                    <span className="ml-2">
                      <StatusBadge status={part.status} />
                    </span>
                  </div>
                  
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-400">Método:</span>
                      <span className="ml-1 font-medium text-white">
                        {part.method === 'CREDIT_CARD' ? 'Cartão de Crédito' : 
                         part.method === 'BOLETO' ? 'Boleto' : part.method}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-400">Valor:</span>
                      <span className="ml-1 font-medium text-primary-500">
                        {planData.currency} {parseFloat(part.amount).toFixed(2)}
                      </span>
                    </div>
                    {part.installments && (
                      <div>
                        <span className="text-gray-400">Parcelas:</span>
                        <span className="ml-1 font-medium text-white">
                          {part.installments}x
                          {part.interest_mode === 'NO_INTEREST' && (
                            <span className="text-green-600 text-xs ml-1">(no interest)</span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {part.expires_at && part.status === 'pending' && (
                    <div className="mt-2 text-sm">
                      <span className="text-gray-400">Expira:</span>
                      <span className="ml-1 font-medium text-white">
                        {new Date(part.expires_at).toLocaleString('pt-BR', { 
                          timeZone: 'America/Sao_Paulo',
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                      {new Date(part.expires_at) < new Date() && (
                        <span className="ml-2 text-red-600 text-xs">(expired)</span>
                      )}
                    </div>
                  )}

                  {part.method === 'BOLETO' && part.payment_link_url && (
                    <div className="mt-3 space-y-3">
                      <div className="bg-dark-800 border border-dark-500 rounded-md p-4">
                        <p className="text-xs font-medium text-gray-400 mb-2">Linha Digitável do Boleto:</p>
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            readOnly
                            value={part.boleto_barcode || 'Gerando código de barras...'}
                            className="flex-1 text-sm font-mono border border-dark-500 rounded-md px-3 py-2 bg-dark-700 text-white"
                          />
                          <button
                            onClick={() => copyToClipboard(part.boleto_barcode || part.payment_link_url, index)}
                            className="btn-secondary inline-flex items-center"
                            title="Copiar código de barras"
                          >
                            {copiedIndex === index ? (
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <a
                          href={part.payment_link_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 btn-primary inline-flex items-center justify-center"
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Baixar Boleto PDF
                        </a>
                        <button
                          onClick={() => copyToClipboard(part.payment_link_url, index + 1000)}
                          className="btn-secondary inline-flex items-center"
                          title="Copiar link do boleto"
                        >
                          {copiedIndex === index + 1000 ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {part.method !== 'BOLETO' && part.payment_link_url && (
                    <div className="mt-3 flex items-center space-x-2">
                      <input
                        type="text"
                        readOnly
                        value={part.payment_link_url}
                        className="flex-1 text-sm border border-dark-500 rounded-md px-3 py-2 bg-dark-600 text-white"
                      />
                      <button
                        onClick={() => copyToClipboard(part.payment_link_url, index)}
                        className="btn-secondary inline-flex items-center"
                      >
                        {copiedIndex === index ? (
                          <>
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4" />
                          </>
                        )}
                      </button>
                      <a
                        href={part.payment_link_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-secondary inline-flex items-center"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </div>
                  )}

                  {part.paid_at && (
                    <p className="mt-2 text-xs text-gray-400">
                      Paid at: {new Date(part.paid_at).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      </div>

      {planData.status === 'paid' && (
        <div className="mt-6 alert-success">
          <div className="flex">
            <CheckCircle className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-200">
                Pagamento Completo
              </h3>
              <p className="mt-1 text-sm text-green-300">
                Todas as parcelas foram recebidas. O pedido foi marcado como pago.
              </p>
              {planData.paid_at && (
                <p className="mt-1 text-xs text-green-400">
                  Completed at: {new Date(planData.paid_at).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentStatus;
